import { OCR_ACCEPT } from '../../core/config.js';
import { userMessage } from '../../core/errors.js';
import { normalizeDocument } from '../../contracts/ocr-document.js';
import { createOcrJob, waitForOcrJob, translateOcrDocument, cancelOcrJob } from '../../services/ocr-api.js';
import { clipboardImageFiles, validateFiles, validatePublicUrl } from './image-input.js';
import { documentConfidence } from './confidence-policy.js';
import { OverlayRenderer } from '../image-overlay/overlay-renderer.js';

const LANGUAGES = [
  ['auto', 'Détection automatique'], ['en', 'English'], ['fr', 'Français'], ['es', 'Español'],
  ['es-ec', 'Español (EC)'], ['tr', 'Türkçe'], ['de', 'Deutsch'], ['it', 'Italiano'],
  ['pt', 'Português'], ['ar', 'العربية'], ['zh', '中文'], ['ja', '日本語'], ['ru', 'Русский'], ['hi', 'हिन्दी']
];

const LANGUAGE_FLAGS = {
  en: 'gb', fr: 'fr', es: 'es', 'es-ec': 'ec', tr: 'tr', de: 'de', it: 'it',
  pt: 'pt', ar: 'sa', zh: 'cn', ja: 'jp', ru: 'ru', hi: 'in'
};

function visibleLanguageName(name) {
  return String(name || '').replace(/\s*\([A-Z]{2}\)\s*$/u, '');
}

function languageFlag(value, name) {
  if (value === 'auto') {
    return '<span class="ocr-language-auto-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18"/></svg></span>';
  }
  const flag = LANGUAGE_FLAGS[value] || LANGUAGE_FLAGS[value.split('-')[0]];
  return `<img class="ocr-language-flag" src="https://flagcdn.com/${flag}.svg" alt="Drapeau — ${visibleLanguageName(name)}" decoding="async">`;
}

const STATUS_COPY = {
  queued: 'En attente du moteur OCR…',
  capturing: 'Capture sécurisée de la page web…',
  ocr: 'Lecture et positionnement du texte…',
  translating: 'Traduction contextuelle de la page…',
  validating: 'Contrôle des régions, nombres et prix…',
  ready: 'Document prêt.',
  failed: 'Le traitement a échoué.',
  cancelled: 'Traitement annulé.'
};

const STATUS_PROGRESS = { queued: 18, capturing: 34, ocr: 58, translating: 78, validating: 92, ready: 100 };

function optionList({ includeAuto = false } = {}) {
  return LANGUAGES.filter(([id]) => includeAuto || id !== 'auto')
    .map(([id, name]) => `<option value="${id}">${name}</option>`).join('');
}

function template() {
  return `
    <section id="ocrModePanel" class="ocr-mode-panel hidden" aria-labelledby="ocrModeTitle">
      <div class="ocr-head">
        <div>
          <h2 id="ocrModeTitle" class="section-title">Image / document</h2>
          <p>Importez une image, un PDF ou une page web. Le texte reste attaché à sa position d’origine.</p>
        </div>
        <span class="ocr-private-badge">Suppression automatique après 15 min</span>
      </div>

      <form id="ocrForm" class="ocr-import-card">
        <div class="ocr-source-tabs" role="tablist" aria-label="Source du document">
          <button class="ocr-source-tab active" id="ocrFilesTab" type="button" role="tab" aria-selected="true">Fichiers</button>
          <button class="ocr-source-tab" id="ocrUrlTab" type="button" role="tab" aria-selected="false">Page web</button>
        </div>
        <div id="ocrFilesPane" class="ocr-source-pane">
          <label class="ocr-dropzone" id="ocrDropzone" tabindex="0">
            <input id="ocrFiles" type="file" multiple accept="${OCR_ACCEPT}">
            <span class="ocr-dropzone-art" aria-hidden="true">
              <svg viewBox="0 0 112 88">
                <defs>
                  <linearGradient id="ocr-folder-gradient" x1="12" y1="8" x2="98" y2="82" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#818cf8"/><stop offset="1" stop-color="#6d28d9"/>
                  </linearGradient>
                  <linearGradient id="ocr-paper-gradient" x1="45" y1="26" x2="74" y2="68" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#fff"/><stop offset="1" stop-color="#ddd6fe"/>
                  </linearGradient>
                </defs>
                <path d="M10 24c0-6 4.9-11 11-11h23l9 10h38c6.1 0 11 5 11 11v35c0 6.1-4.9 11-11 11H21c-6.1 0-11-4.9-11-11V24Z" fill="url(#ocr-folder-gradient)"/>
                <path d="M11 37h90v32c0 6.1-4.9 11-11 11H22c-6.1 0-11-4.9-11-11V37Z" fill="#4f46e5" opacity=".54"/>
                <path d="M40 30a5 5 0 0 1 5-5h22l12 12v29a5 5 0 0 1-5 5H45a5 5 0 0 1-5-5V30Z" fill="url(#ocr-paper-gradient)"/>
                <path d="M67 25v12h12" fill="none" stroke="#a78bfa" stroke-width="3" stroke-linejoin="round"/>
                <path d="M59.5 59V42m0 0-7 7m7-7 7 7" fill="none" stroke="#6d28d9" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <strong>Glissez-déposez ou collez vos fichiers ici</strong>
            <span class="ocr-dropzone-copy">Ctrl+V pour coller une image, ou <span class="ocr-browse-copy">choisissez des fichiers</span></span>
            <small>Images, HEIC, AVIF, TIFF, SVG et PDF · 20 MiB maximum par fichier</small>
          </label>
          <button id="ocrPasteTarget" class="ocr-paste-target" type="button">
            <span class="ocr-paste-icon" aria-hidden="true">⌘</span>
            <span><strong>Cliquez ici, puis appuyez sur Ctrl+V</strong><small>Vous pouvez aussi cliquer pour coller directement depuis le presse-papiers</small></span>
          </button>
          <div id="ocrQueueActions" class="ocr-queue-actions hidden">
            <strong>File d’attente</strong>
            <span class="ocr-queue-buttons">
              <button id="ocrStartQueue" class="ocr-start-queue" type="button">Lancer l’analyse</button>
              <button id="ocrClearQueue" type="button">Vider la file</button>
            </span>
          </div>
          <div id="ocrFileList" class="ocr-file-list" role="list" aria-live="polite"></div>
        </div>
        <div id="ocrUrlPane" class="ocr-source-pane hidden">
          <label for="ocrUrl">URL publique HTTP ou HTTPS</label>
          <input id="ocrUrl" type="url" inputmode="url" placeholder="https://exemple.com/article" autocomplete="url">
          <p>Aucun cookie ni identifiant n’est transmis à la page capturée.</p>
        </div>
        <div class="ocr-options">
          <div class="ocr-quality-field">
            <span class="ocr-quality-label" id="ocrSourceLanguageLabel">Langue source</span>
            <select id="ocrSourceLanguage" class="ocr-native-select" tabindex="-1" aria-hidden="true">${optionList({ includeAuto: true })}</select>
            <div id="ocrSourceLanguageDropdown" class="ocr-quality-dropdown ocr-language-dropdown"></div>
          </div>
          <div class="ocr-quality-field">
            <span class="ocr-quality-label" id="ocrTargetLanguageLabel">Langue cible</span>
            <select id="ocrTargetLanguage" class="ocr-native-select" tabindex="-1" aria-hidden="true">${optionList()}</select>
            <div id="ocrTargetLanguageDropdown" class="ocr-quality-dropdown ocr-language-dropdown"></div>
          </div>
          <div class="ocr-quality-field">
            <span class="ocr-quality-label" id="ocrQualityLabel">Qualité</span>
            <select id="ocrMode" class="ocr-native-select" tabindex="-1" aria-hidden="true"><option value="smart">Smart · contrôles automatiques</option><option value="fast">Fast · plus rapide</option></select>
            <div id="ocrQualityDropdown" class="ocr-quality-dropdown">
              <button id="ocrQualityTrigger" class="ocr-quality-trigger" type="button" aria-haspopup="listbox" aria-expanded="false" aria-labelledby="ocrQualityLabel ocrQualityValue">
                <span id="ocrQualityValue" class="ocr-quality-value">
                  <svg class="ocr-quality-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3-1.91 5.81a2 2 0 0 1-1.28 1.28L3 12l5.81 1.91a2 2 0 0 1 1.28 1.28L12 21l1.91-5.81a2 2 0 0 1 1.28-1.28L21 12l-5.81-1.91a2 2 0 0 1-1.28-1.28L12 3Z"/></svg>
                  <span><strong>Smart</strong><small>contrôles automatiques</small></span>
                </span>
                <svg class="ocr-quality-chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
              </button>
              <ul id="ocrQualityMenu" class="ocr-quality-menu" role="listbox" aria-labelledby="ocrQualityLabel">
                <li class="ocr-quality-option selected" role="option" aria-selected="true" tabindex="-1" data-value="smart">
                  <span class="ocr-quality-icon-box"><svg class="ocr-quality-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3-1.91 5.81a2 2 0 0 1-1.28 1.28L3 12l5.81 1.91a2 2 0 0 1 1.28 1.28L12 21l1.91-5.81a2 2 0 0 1 1.28-1.28L21 12l-5.81-1.91a2 2 0 0 1-1.28-1.28L12 3Z"/></svg></span>
                  <span class="ocr-quality-copy"><strong>Smart</strong><small>Contrôles automatiques</small></span>
                  <svg class="ocr-quality-check" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>
                </li>
                <li class="ocr-quality-option" role="option" aria-selected="false" tabindex="-1" data-value="fast">
                  <span class="ocr-quality-icon-box"><svg class="ocr-quality-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z"/></svg></span>
                  <span class="ocr-quality-copy"><strong>Fast</strong><small>Plus rapide</small></span>
                  <svg class="ocr-quality-check" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div id="ocrFormError" class="ocr-form-error hidden" role="alert"></div>
        <div class="ocr-submit-row">
          <button id="ocrSubmit" class="primary-button" type="submit">Lire et traduire</button>
          <button id="ocrCancel" class="secondary-button hidden" type="button">Annuler</button>
        </div>
      </form>

      <div id="ocrProgress" class="ocr-progress hidden" role="status" aria-live="polite">
        <div class="ocr-spinner" aria-hidden="true"></div>
        <div><strong id="ocrProgressTitle">Préparation…</strong><span id="ocrProgressMeta"></span></div>
      </div>

      <section id="ocrResult" class="ocr-result hidden" aria-label="Résultat OCR traduit">
        <div class="ocr-toolbar">
          <div class="ocr-page-nav">
            <button id="ocrPrevPage" class="secondary-button" type="button">←</button>
            <span id="ocrPageLabel">Page 1 / 1</span>
            <button id="ocrNextPage" class="secondary-button" type="button">→</button>
          </div>
          <label>Original <input id="ocrOpacity" type="range" min="0" max="1" step="0.05" value="0.92"> Traduction</label>
          <div class="ocr-transform-actions">
            <button id="ocrZoomOut" class="secondary-button" type="button" aria-label="Réduire">−</button>
            <button id="ocrZoomIn" class="secondary-button" type="button" aria-label="Agrandir">+</button>
            <button id="ocrRotate" class="secondary-button" type="button">↻</button>
          </div>
        </div>
        <div class="ocr-result-grid">
          <div class="ocr-stage-scroll"><div id="ocrStage" class="ocr-stage"></div></div>
          <aside id="ocrRegionDetails" class="ocr-region-details"><p>Sélectionnez une région de l’image.</p></aside>
        </div>
        <details class="ocr-accessible-list">
          <summary>Lire toutes les régions sous forme de liste</summary>
          <div id="ocrRegionList"></div>
        </details>
      </section>
    </section>`;
}

function fileKey(file) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 o';
  const units = ['o', 'Ko', 'Mo', 'Go'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / (1024 ** index);
  return `${value >= 10 || index === 0 ? Math.round(value) : value.toFixed(1)} ${units[index]}`;
}

function fileVisual(file) {
  const extension = file.name.split('.').pop()?.toLowerCase() || 'file';
  if (extension === 'pdf') return { label: 'PDF', tone: 'red' };
  if (['doc', 'docx'].includes(extension)) return { label: 'DOC', tone: 'blue' };
  if (['xls', 'xlsx', 'csv'].includes(extension)) return { label: 'XLS', tone: 'green' };
  if (['svg'].includes(extension)) return { label: 'SVG', tone: 'orange' };
  if (['heic', 'heif'].includes(extension)) return { label: 'HEIC', tone: 'cyan' };
  if (['tif', 'tiff'].includes(extension)) return { label: 'TIFF', tone: 'violet' };
  return { label: extension.slice(0, 4).toUpperCase() || 'IMG', tone: 'violet' };
}

function fileTypeSvg(meta) {
  return `<svg class="ocr-file-type-svg" viewBox="0 0 48 56" aria-hidden="true">
    <path d="M8 2h21l11 11v35a6 6 0 0 1-6 6H8a6 6 0 0 1-6-6V8a6 6 0 0 1 6-6Z" class="ocr-file-sheet"/>
    <path d="M29 2v11h11" class="ocr-file-fold"/>
    <rect x="7" y="31" width="34" height="16" rx="5" class="ocr-file-label-bg"/>
    <text x="24" y="42.5" text-anchor="middle" class="ocr-file-label">${meta.label}</text>
  </svg>`;
}

function fileStatusSvg(state) {
  if (state === 'success') return `<svg class="ocr-file-status-svg success" viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="14"/><path d="m9.5 16.2 4.2 4.2 8.8-9"/></svg>`;
  if (state === 'error') return `<svg class="ocr-file-status-svg error" viewBox="0 0 32 32" aria-hidden="true"><path d="M25.2 11A11 11 0 1 0 26 19"/><path d="M25 5v7h-7"/></svg>`;
  if (state === 'uploading') return `<svg class="ocr-file-status-svg uploading" viewBox="0 0 32 32" aria-hidden="true"><circle class="track" cx="16" cy="16" r="13" pathLength="100"/><circle class="value" cx="16" cy="16" r="13" pathLength="100"/></svg>`;
  return `<svg class="ocr-file-status-svg ready" viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="13"/><path d="M16 21V11m0 0-4 4m4-4 4 4"/></svg>`;
}

function fileActionSvg(state) {
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10M17 7 7 17"/></svg>`;
}

export function initOcrFeature() {
  const switcher = document.querySelector('.workspace-mode-switch');
  const translator = document.getElementById('translatorModeContent');
  const reading = document.getElementById('readingModePanel');
  if (!switcher || !translator || !reading || document.getElementById('ocrModePanel')) return;

  const imageButton = document.createElement('button');
  imageButton.id = 'imageModeBtn';
  imageButton.className = 'workspace-mode-btn';
  imageButton.type = 'button';
  imageButton.role = 'tab';
  imageButton.setAttribute('aria-selected', 'false');
  imageButton.textContent = 'Image / document';
  switcher.append(imageButton);
  reading.insertAdjacentHTML('afterend', template());

  const panel = document.getElementById('ocrModePanel');
  const translatorButton = document.getElementById('translatorModeBtn');
  const readingButton = document.getElementById('readingModeBtn');
  const setMode = active => {
    panel.classList.toggle('hidden', active !== 'ocr');
    if (active === 'ocr') {
      translator.classList.add('hidden');
      reading.classList.add('hidden');
      translatorButton?.classList.remove('active');
      readingButton?.classList.remove('active');
      translatorButton?.setAttribute('aria-selected', 'false');
      readingButton?.setAttribute('aria-selected', 'false');
    }
    imageButton.classList.toggle('active', active === 'ocr');
    imageButton.setAttribute('aria-selected', String(active === 'ocr'));
  };
  imageButton.addEventListener('click', () => setMode('ocr'));
  translatorButton?.addEventListener('click', () => setMode('translator'));
  readingButton?.addEventListener('click', () => setMode('reading'));

  const elements = Object.fromEntries([
    'ocrForm', 'ocrFilesTab', 'ocrUrlTab', 'ocrFilesPane', 'ocrUrlPane', 'ocrFiles', 'ocrFileList',
    'ocrUrl', 'ocrSourceLanguage', 'ocrTargetLanguage', 'ocrMode', 'ocrFormError', 'ocrSubmit',
    'ocrCancel', 'ocrProgress', 'ocrProgressTitle', 'ocrProgressMeta', 'ocrResult', 'ocrStage', 'ocrDropzone',
    'ocrPasteTarget', 'ocrQueueActions', 'ocrStartQueue', 'ocrClearQueue',
    'ocrQualityDropdown', 'ocrQualityTrigger', 'ocrQualityValue', 'ocrQualityMenu',
    'ocrSourceLanguageDropdown', 'ocrTargetLanguageDropdown',
    'ocrRegionList', 'ocrRegionDetails', 'ocrOpacity', 'ocrPageLabel', 'ocrPrevPage', 'ocrNextPage'
  ].map(id => [id, document.getElementById(id)]));
  elements.ocrTargetLanguage.value = 'fr';

  let inputKind = 'files';
  let abortController = null;
  let activeJobId = null;
  let activeDocument = null;
  let pageIndex = 0;
  let zoom = 1;
  let rotation = 0;
  let selectedFiles = [];
  let progressTimer = null;
  const fileCards = new Map();

  const renderer = new OverlayRenderer({
    stage: elements.ocrStage,
    list: elements.ocrRegionList,
    details: elements.ocrRegionDetails,
    onSpeak: text => speak(text, elements.ocrTargetLanguage.value),
    onSave: region => saveRegion(region, elements.ocrSourceLanguage.value, elements.ocrTargetLanguage.value)
  });

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []);
    const known = new Set(selectedFiles.map(fileKey));
    for (const file of incoming) {
      if (known.has(fileKey(file))) continue;
      if (selectedFiles.length >= 10) {
        showError('Maximum 10 fichiers par traitement.');
        break;
      }
      selectedFiles.push(file);
      known.add(fileKey(file));
      appendFileCard(file);
    }
    elements.ocrDropzone.classList.toggle('has-files', selectedFiles.length > 0);
    elements.ocrQueueActions.classList.toggle('hidden', selectedFiles.length === 0);
  }

  function clearQueue() {
    selectedFiles = [];
    fileCards.forEach(record => {
      if (record.previewUrl) URL.revokeObjectURL(record.previewUrl);
      record.row.remove();
    });
    fileCards.clear();
    elements.ocrDropzone.classList.remove('has-files');
    elements.ocrQueueActions.classList.add('hidden');
    clearError();
  }

  function appendFileCard(file) {
    const key = fileKey(file);
    const visual = fileVisual(file);
    const previewUrl = file.type.startsWith('image/') && file.type !== 'image/svg+xml'
      ? URL.createObjectURL(file)
      : '';
    const row = document.createElement('article');
    row.className = `ocr-file-item ready tone-${visual.tone}`;
    row.setAttribute('role', 'listitem');
    row.innerHTML = `
      <span class="ocr-file-icon">${previewUrl ? '<img class="ocr-file-preview" alt="">' : fileTypeSvg(visual)}</span>
      <span class="ocr-file-body">
        <span class="ocr-file-head"><strong class="ocr-file-name"></strong><span class="ocr-file-percent">En attente</span></span>
        <span class="ocr-file-meta"><span class="ocr-file-message">Dans la file — lancez l’analyse</span><span>${formatBytes(file.size)}</span></span>
        <span class="ocr-file-progress" aria-hidden="true"><span></span></span>
      </span>
      <span class="ocr-file-status">${fileStatusSvg('ready')}</span>
      <button class="ocr-file-action" type="button" aria-label="Retirer le fichier">${fileActionSvg('ready')}</button>`;
    row.querySelector('.ocr-file-name').textContent = file.name;
    if (previewUrl) row.querySelector('.ocr-file-preview').src = previewUrl;
    const record = { key, file, row, visual, state: 'ready', progress: 0, previewUrl };
    fileCards.set(key, record);
    row.querySelector('.ocr-file-action').addEventListener('click', () => {
      selectedFiles = selectedFiles.filter(item => fileKey(item) !== key);
      fileCards.delete(key);
      if (record.previewUrl) URL.revokeObjectURL(record.previewUrl);
      row.classList.add('removing');
      row.addEventListener('animationend', () => row.remove(), { once: true });
      elements.ocrDropzone.classList.toggle('has-files', selectedFiles.length > 0);
      elements.ocrQueueActions.classList.toggle('hidden', selectedFiles.length === 0);
    });
    elements.ocrFileList.append(row);
  }

  function updateFileCard(record, state, progress, message) {
    if (!record) return;
    const bounded = Math.max(0, Math.min(100, Math.round(progress)));
    record.state = state;
    record.progress = bounded;
    record.row.className = `ocr-file-item ${state} tone-${record.visual.tone}`;
    record.row.style.setProperty('--file-progress', String(bounded));
    record.row.querySelector('.ocr-file-percent').textContent = `${bounded}%`;
    record.row.querySelector('.ocr-file-message').textContent = message;
    record.row.querySelector('.ocr-file-status').innerHTML = fileStatusSvg(state);
    const action = record.row.querySelector('.ocr-file-action');
    action.innerHTML = fileActionSvg(state);
    action.setAttribute('aria-label', 'Retirer le fichier');
    action.title = 'Retirer';
  }

  function updateAllFileCards(state, progress, message) {
    fileCards.forEach(record => updateFileCard(record, state, progress, message));
  }

  function startProgressAnimation() {
    clearInterval(progressTimer);
    progressTimer = setInterval(() => {
      fileCards.forEach(record => {
        if (record.state !== 'uploading' || record.progress >= 68) return;
        updateFileCard(record, 'uploading', record.progress + 1, 'Envoi sécurisé et préparation…');
      });
    }, 240);
  }

  function stopProgressAnimation() {
    clearInterval(progressTimer);
    progressTimer = null;
  }

  function switchSource(kind) {
    inputKind = kind;
    const files = kind === 'files';
    elements.ocrFilesPane.classList.toggle('hidden', !files);
    elements.ocrUrlPane.classList.toggle('hidden', files);
    elements.ocrFilesTab.classList.toggle('active', files);
    elements.ocrUrlTab.classList.toggle('active', !files);
    elements.ocrFilesTab.setAttribute('aria-selected', String(files));
    elements.ocrUrlTab.setAttribute('aria-selected', String(!files));
    clearError();
  }

  function showError(message) {
    elements.ocrFormError.textContent = message;
    elements.ocrFormError.classList.remove('hidden');
  }
  function clearError() {
    elements.ocrFormError.textContent = '';
    elements.ocrFormError.classList.add('hidden');
  }
  function setBusy(busy) {
    elements.ocrSubmit.disabled = busy;
    elements.ocrStartQueue.disabled = busy;
    elements.ocrClearQueue.disabled = busy;
    elements.ocrCancel.classList.toggle('hidden', !busy);
    elements.ocrProgress.classList.toggle('hidden', !busy);
    fileCards.forEach(record => {
      const action = record.row.querySelector('.ocr-file-action');
      action.disabled = busy;
    });
  }
  function updateProgress(status, meta = '') {
    elements.ocrProgressTitle.textContent = STATUS_COPY[status] || status;
    elements.ocrProgressMeta.textContent = meta;
    if (inputKind === 'files' && STATUS_PROGRESS[status] != null) {
      const state = status === 'ready' ? 'success' : 'uploading';
      updateAllFileCards(state, STATUS_PROGRESS[status], STATUS_COPY[status] || status);
    }
  }

  function enhanceLanguageSelect(select, dropdown, labelId, iconKind) {
    const languageIcon = iconKind === 'source'
      ? '<svg class="ocr-quality-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18"/></svg>'
      : '<svg class="ocr-quality-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 8 6 6M4 14l6-6 2-3M2 5h12M7 2v3M13 22l4-9 4 9M14.5 19h5"/></svg>';
    dropdown.innerHTML = `
      <button class="ocr-quality-trigger" type="button" aria-haspopup="listbox" aria-expanded="false" aria-labelledby="${labelId}">
        <span class="ocr-quality-value">${languageIcon}<span><strong></strong><small>${iconKind === 'source' ? 'Langue du document' : 'Langue de traduction'}</small></span></span>
        <svg class="ocr-quality-chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
      </button>
      <ul class="ocr-quality-menu ocr-language-menu" role="listbox" aria-labelledby="${labelId}"></ul>`;
    const trigger = dropdown.querySelector('.ocr-quality-trigger');
    const valueTitle = dropdown.querySelector('.ocr-quality-value strong');
    const menu = dropdown.querySelector('.ocr-quality-menu');
    Array.from(select.options).forEach(nativeOption => {
      const option = document.createElement('li');
      option.className = 'ocr-quality-option ocr-language-option';
      option.role = 'option';
      option.tabIndex = -1;
      option.dataset.value = nativeOption.value;
      option.innerHTML = `${languageFlag(nativeOption.value, nativeOption.text)}<span class="ocr-quality-copy"><strong></strong><small>${nativeOption.value === 'auto' ? 'Reconnaissance automatique' : 'Langue disponible'}</small></span><svg class="ocr-quality-check" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>`;
      option.querySelector('.ocr-quality-copy strong').textContent = visibleLanguageName(nativeOption.text);
      menu.append(option);
    });
    const options = Array.from(menu.children);
    const close = () => {
      dropdown.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
    };
    const sync = (value, returnFocus = false) => {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      options.forEach(option => {
        const selected = option.dataset.value === value;
        option.classList.toggle('selected', selected);
        option.setAttribute('aria-selected', String(selected));
      });
      const selectedNative = select.selectedOptions[0];
      valueTitle.textContent = visibleLanguageName(selectedNative?.textContent || value);
      const valueWrap = dropdown.querySelector('.ocr-quality-value');
      valueWrap.querySelector('.ocr-quality-icon, .ocr-language-flag, .ocr-language-auto-icon')?.remove();
      valueWrap.insertAdjacentHTML('afterbegin', languageFlag(value, selectedNative?.textContent || value));
      close();
      if (returnFocus) trigger.focus();
    };
    const open = () => {
      dropdown.classList.add('open');
      trigger.setAttribute('aria-expanded', 'true');
      (options.find(option => option.classList.contains('selected')) || options[0]).focus();
    };
    trigger.addEventListener('click', () => dropdown.classList.contains('open') ? close() : open());
    trigger.addEventListener('keydown', event => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      open();
    });
    options.forEach((option, index) => {
      option.addEventListener('click', () => sync(option.dataset.value, true));
      option.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault(); close(); trigger.focus();
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault(); sync(option.dataset.value, true);
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const offset = event.key === 'ArrowDown' ? 1 : -1;
          options[(index + offset + options.length) % options.length].focus();
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); options[event.key === 'Home' ? 0 : options.length - 1].focus();
        }
      });
    });
    document.addEventListener('click', event => {
      if (!dropdown.contains(event.target)) close();
    });
    sync(select.value);
  }

  enhanceLanguageSelect(elements.ocrSourceLanguage, elements.ocrSourceLanguageDropdown, 'ocrSourceLanguageLabel', 'source');
  enhanceLanguageSelect(elements.ocrTargetLanguage, elements.ocrTargetLanguageDropdown, 'ocrTargetLanguageLabel', 'target');

  const qualityOptions = Array.from(elements.ocrQualityMenu.querySelectorAll('.ocr-quality-option'));
  function setQualityOpen(open, focusSelected = false) {
    elements.ocrQualityDropdown.classList.toggle('open', open);
    elements.ocrQualityTrigger.setAttribute('aria-expanded', String(open));
    if (open && focusSelected) {
      (qualityOptions.find(option => option.classList.contains('selected')) || qualityOptions[0]).focus();
    }
  }
  function selectQuality(option, returnFocus = true) {
    if (!option) return;
    qualityOptions.forEach(item => {
      const selected = item === option;
      item.classList.toggle('selected', selected);
      item.setAttribute('aria-selected', String(selected));
    });
    elements.ocrMode.value = option.dataset.value;
    elements.ocrMode.dispatchEvent(new Event('change', { bubbles: true }));
    const icon = option.querySelector('.ocr-quality-icon').cloneNode(true);
    const title = option.querySelector('.ocr-quality-copy strong').textContent;
    const description = option.querySelector('.ocr-quality-copy small').textContent;
    elements.ocrQualityValue.replaceChildren(icon, Object.assign(document.createElement('span'), {
      innerHTML: `<strong>${title}</strong><small>${description.toLowerCase()}</small>`
    }));
    setQualityOpen(false);
    if (returnFocus) elements.ocrQualityTrigger.focus();
  }
  elements.ocrQualityTrigger.addEventListener('click', () => {
    const open = !elements.ocrQualityDropdown.classList.contains('open');
    setQualityOpen(open, open);
  });
  elements.ocrQualityTrigger.addEventListener('keydown', event => {
    if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    setQualityOpen(true, true);
  });
  qualityOptions.forEach((option, index) => {
    option.addEventListener('click', () => selectQuality(option));
    option.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setQualityOpen(false);
        elements.ocrQualityTrigger.focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        selectQuality(option);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const offset = event.key === 'ArrowDown' ? 1 : -1;
        qualityOptions[(index + offset + qualityOptions.length) % qualityOptions.length].focus();
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        qualityOptions[event.key === 'Home' ? 0 : qualityOptions.length - 1].focus();
      }
    });
  });
  document.addEventListener('click', event => {
    if (!elements.ocrQualityDropdown.contains(event.target)) setQualityOpen(false);
  });

  elements.ocrFilesTab.addEventListener('click', () => switchSource('files'));
  elements.ocrUrlTab.addEventListener('click', () => switchSource('url'));
  elements.ocrFiles.addEventListener('change', () => {
    addFiles(elements.ocrFiles.files);
    elements.ocrFiles.value = '';
  });
  elements.ocrDropzone.addEventListener('dragover', event => {
    event.preventDefault();
    elements.ocrDropzone.classList.add('dragging');
  });
  elements.ocrDropzone.addEventListener('dragleave', () => elements.ocrDropzone.classList.remove('dragging'));
  elements.ocrDropzone.addEventListener('drop', event => {
    event.preventDefault();
    elements.ocrDropzone.classList.remove('dragging');
    if (!event.dataTransfer?.files?.length) return;
    addFiles(event.dataTransfer.files);
  });
  elements.ocrDropzone.addEventListener('keydown', event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    elements.ocrFiles.click();
  });

  async function readClipboardImages() {
    const images = [];
    if (!navigator.clipboard?.read) return images;
    const clipboardItems = await navigator.clipboard.read();
    for (const item of clipboardItems) {
      for (const type of item.types.filter(type => type.startsWith('image/'))) {
        const blob = await item.getType(type);
        const extension = type.split('/')[1].replace('jpeg', 'jpg').replace('svg+xml', 'svg');
        images.push(new File([blob], `image-collee-${Date.now()}-${images.length + 1}.${extension}`, { type }));
      }
    }
    return images;
  }

  async function queueClipboardImages(images) {
    if (!images.length) return false;
    clearError();
    switchSource('files');
    addFiles(images);
    elements.ocrPasteTarget.classList.add('pasted');
    setTimeout(() => elements.ocrPasteTarget.classList.remove('pasted'), 650);
    return true;
  }

  elements.ocrPasteTarget.addEventListener('click', async () => {
    elements.ocrPasteTarget.focus();
    try {
      const images = await readClipboardImages();
      if (!await queueClipboardImages(images)) {
        showError('Aucune image accessible dans le presse-papiers. Copiez une image, cliquez ici, puis faites Ctrl+V.');
      }
    } catch {
      showError('Le navigateur bloque la lecture directe. Gardez cette zone sélectionnée et appuyez sur Ctrl+V.');
    }
  });
  elements.ocrClearQueue.addEventListener('click', clearQueue);
  elements.ocrStartQueue.addEventListener('click', () => elements.ocrForm.requestSubmit());

  document.addEventListener('paste', async event => {
    if (panel.classList.contains('hidden')) return;
    let images = clipboardImageFiles(event.clipboardData);
    const clipboardContainsImage = Array.from(event.clipboardData?.types || []).some(type =>
      type.startsWith('image/') || type === 'text/html'
    );
    if (images.length || clipboardContainsImage) event.preventDefault();

    if (!images.length && navigator.clipboard?.read) {
      try {
        images = await readClipboardImages();
      } catch {
        // Clipboard API access is optional; the paste-event files remain the primary path.
      }
    }

    if (!images.length) {
      if (clipboardContainsImage) showError('L’image copiée n’est pas accessible. Utilisez « Copier l’image » puis Ctrl+V, ou glissez-la ici.');
      return;
    }
    await queueClipboardImages(images);
  }, true);

  elements.ocrForm.addEventListener('submit', async event => {
    event.preventDefault();
    clearError();
    const files = [...selectedFiles];
    const url = elements.ocrUrl.value.trim();
    const errors = inputKind === 'files' ? validateFiles(files) : [validatePublicUrl(url)].filter(Boolean);
    if (errors.length) {
      updateAllFileCards('error', 0, 'Fichier refusé — vérifiez le format ou la taille');
      return showError(errors.join(' '));
    }

    abortController = new AbortController();
    setBusy(true);
    elements.ocrResult.classList.add('hidden');
    updateProgress('queued');
    startProgressAnimation();
    try {
      const options = {
        files: inputKind === 'files' ? files : [],
        url: inputKind === 'url' ? url : '',
        sourceLanguage: elements.ocrSourceLanguage.value,
        targetLanguage: elements.ocrTargetLanguage.value,
        mode: elements.ocrMode.value
      };
      const job = await createOcrJob(options, abortController.signal);
      activeJobId = job.jobId;
      const ocrDocument = job.status === 'ready' && job.document
        ? normalizeDocument(job.document)
        : await waitForOcrJob(job.jobId, {
            signal: abortController.signal,
            onUpdate: update => updateProgress(update.status, update.progress ? `${update.progress} %` : '')
          });
      updateProgress('translating');
      activeDocument = await translateOcrDocument(ocrDocument, options, abortController.signal);
      updateProgress('validating', `Confiance OCR moyenne ${Math.round(documentConfidence(activeDocument) * 100)} %`);
      pageIndex = 0;
      renderer.setDocument(activeDocument);
      updatePageControls();
      elements.ocrResult.classList.remove('hidden');
      updateProgress('ready');
    } catch (error) {
      if (error.name !== 'AbortError') {
        const message = userMessage(error);
        updateAllFileCards('error', Math.max(8, ...Array.from(fileCards.values()).map(record => record.progress)), message);
        showError(message);
      }
    } finally {
      stopProgressAnimation();
      setBusy(false);
      abortController = null;
      activeJobId = null;
    }
  });

  elements.ocrCancel.addEventListener('click', async () => {
    abortController?.abort();
    if (activeJobId) await cancelOcrJob(activeJobId).catch(() => null);
    stopProgressAnimation();
    updateAllFileCards('ready', 0, 'Traitement annulé — prêt à relancer');
    setBusy(false);
    updateProgress('cancelled');
  });

  function updatePageControls() {
    const count = activeDocument?.pages.length || 0;
    elements.ocrPageLabel.textContent = `Page ${pageIndex + 1} / ${count}`;
    elements.ocrPrevPage.disabled = pageIndex <= 0;
    elements.ocrNextPage.disabled = pageIndex >= count - 1;
  }
  elements.ocrPrevPage.addEventListener('click', () => {
    if (pageIndex > 0) renderer.setPage(--pageIndex);
    updatePageControls();
  });
  elements.ocrNextPage.addEventListener('click', () => {
    if (activeDocument && pageIndex < activeDocument.pages.length - 1) renderer.setPage(++pageIndex);
    updatePageControls();
  });
  elements.ocrOpacity.addEventListener('input', event => renderer.setOpacity(event.target.value));
  document.getElementById('ocrZoomIn').addEventListener('click', () => renderer.setTransform({ zoom: zoom = Math.min(2.5, zoom + 0.2), rotation }));
  document.getElementById('ocrZoomOut').addEventListener('click', () => renderer.setTransform({ zoom: zoom = Math.max(0.6, zoom - 0.2), rotation }));
  document.getElementById('ocrRotate').addEventListener('click', () => renderer.setTransform({ zoom, rotation: rotation = (rotation + 90) % 360 }));
}

function speak(text, language) {
  if (!text || !('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = language;
  window.speechSynthesis.speak(utterance);
}

function saveRegion(region, sourceLang, targetLang) {
  const key = 'chibilingo_history_v2';
  let history = [];
  try { history = JSON.parse(localStorage.getItem(key)) || []; } catch { history = []; }
  history.push({
    source: region.text,
    translation: region.translation.text,
    sourceLang,
    targetLang,
    result: { traduction: region.translation.text, definition: '', prononciation: '' },
    timestamp: Date.now(),
    origin: 'ocr'
  });
  localStorage.setItem(key, JSON.stringify(history));
  window.dispatchEvent(new CustomEvent('translucide:history-changed'));
}
